import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  ApplicationFormFields,
  applicable,
} from "../../../../shared/src/application-forms/ApplicationFormFields";
import {
  FormSnapshotSummary,
  saveAttachment,
} from "../../../../shared/src/application-forms/FormResponseCard";
import {
  Answers,
  Field,
  FormSchema,
  Row,
  Upload,
  formError,
} from "../../../../shared/src/application-forms/types";
import { StepIndicator } from "@/components/auth/StepIndicator";
import { agentApi } from "@/services/agentApi";
import { SavedProfilePicker } from "./steps/SavedProfilePicker";
import { fillFromSavedProfile } from "./savedProfileAnswers";

function keepAnswers(
  old: FormSchema | undefined,
  next: FormSchema,
  answers: Answers,
  retainedFiles: Upload[] = [],
): Answers {
  const result: Answers = {};
  const keep = (fields: Field[], before: Field[], row: Row | Answers): Row => {
    const saved: Row = {};
    for (const field of fields) {
      const value = row[field.id];
      const previous = before.find((item) => item.id === field.id);
      if (value === undefined || !previous || !applicable(field, row)) continue;
      if (field.type === "file") {
        const compatible =
          previous.type === "file" &&
          (field.maxBytes ?? 0) >= (previous.maxBytes ?? 0) &&
          previous.mimeTypes?.every((type) => field.mimeTypes?.includes(type));
        if (compatible && retainedFiles.some((file) => file.id === value))
          saved[field.id] = value as string;
        continue;
      }
      if (
        field.type === "radio" ||
        field.type === "select" ||
        field.type === "multiSelect"
      ) {
        const findOption = (id: string) => {
          const label =
            previous.options?.find((option) => option.id === id)?.label ?? id;
          return (
            field.options?.find((option) => option.id === id)?.id ??
            field.options?.find(
              (option) =>
                option.label.trim().toLowerCase() ===
                label.trim().toLowerCase(),
            )?.id
          );
        };
        if (field.type === "multiSelect" && Array.isArray(value)) {
          saved[field.id] = [
            ...new Set(
              value
                .filter((id): id is string => typeof id === "string")
                .map(findOption)
                .filter((id): id is string => Boolean(id)),
            ),
          ];
        } else if (field.type !== "multiSelect" && typeof value === "string") {
          const option = findOption(value);
          if (option) saved[field.id] = option;
        }
      } else if (
        previous.type === field.type &&
        previous.termsUrl === field.termsUrl
      )
        saved[field.id] = value as Row[string];
    }
    return saved;
  };
  const priorFields =
    old?.sections
      .filter((section) => !section.repeat)
      .flatMap((section) => section.fields) || [];
  for (const section of next.sections) {
    const before = old?.sections.find((item) => item.id === section.id);
    if (section.repeat) {
      if (!before?.repeat) continue;
      const rows = answers[section.id] as Row[] | undefined;
      result[section.id] = (rows || [])
        .slice(0, section.repeat.max)
        .map((row) => keep(section.fields, before.fields, row));
    } else Object.assign(result, keep(section.fields, priorFields, answers));
  }
  return result;
}

function visibleAnswers(schema: FormSchema, answers: Answers): Answers {
  const visible = (fields: Field[], row: Answers | Row) =>
    Object.fromEntries(
      fields
        .filter(
          (field) =>
            field.type !== "termsAgreement" &&
            applicable(field, row) &&
            row[field.id] !== undefined,
        )
        .map((field) => [field.id, row[field.id]]),
    );
  return Object.assign(
    {},
    ...schema.sections.map((section) =>
      section.repeat
        ? {
            [section.id]: (
              (answers[section.id] as Row[] | undefined) || []
            ).map((row) => visible(section.fields, row)),
          }
        : visible(section.fields, answers),
    ),
  ) as Answers;
}

export function ConfiguredApplicationForm({
  universityId,
  initialOffering,
  applicationId,
  revision,
  onBack,
}: {
  universityId: number;
  initialOffering?: number;
  applicationId?: number;
  revision: { id: number; schema: FormSchema };
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<number>(() => {
    const savedStep = location.state?.configuredStep;
    return applicationId &&
      Number.isInteger(savedStep) &&
      savedStep > 0 &&
      savedStep <= revision.schema.sections.length + 3
      ? savedStep
      : 1;
  });
  const stepStart = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(revision);
  const stepLabels = [
    "Programme and intake",
    "Load Profile",
    ...current.schema.sections.map((section) => section.label),
    "Preview",
  ];
  const reviewStep = stepLabels.length;
  const activeSection = current.schema.sections[step - 3];
  const goToStep = (next: number) => {
    setStep(next);
    stepStart.current?.focus();
    stepStart.current?.scrollIntoView({ block: "start" });
  };
  const [offering, setOffering] = useState(initialOffering || 0);
  const [intake, setIntake] = useState("");
  const [answers, setAnswers] = useState<Answers>({});
  const profileFill = useRef<{ base: Answers; result: Answers }>();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const stagedFiles = useRef(new Map<string, File>());
  const [attached, setAttached] = useState<Upload[]>([]);
  const [draftId, setDraftId] = useState(applicationId);
  const [draftKey, setDraftKey] = useState<string>(() => crypto.randomUUID());
  const [pending, setPending] = useState(false);
  const [uncertainSubmission, setUncertainSubmission] = useState<number>();
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(Boolean(applicationId));
  const [loadFailed, setLoadFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const status = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!applicationId) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadFailed(false);
    void agentApi
      .getApplicationFormResponse(applicationId, controller.signal)
      .then((saved) => {
        if (!saved || controller.signal.aborted) return;
        if (saved.draftKey) setDraftKey(saved.draftKey);
        const currentIntakes =
          revision.schema.programmes.find(
            (programme) => programme.offeringId === initialOffering,
          )?.intakes || [];
        setIntake(currentIntakes.includes(saved.intake) ? saved.intake : "");
        if (saved.revisionId === revision.id && saved.draftKey) {
          setAnswers(saved.answers);
          setUploads(saved.uploads);
          setAttached(saved.uploads);
        } else {
          const reusableUploads = saved.draftKey ? saved.uploads : [];
          setAnswers(
            keepAnswers(
              saved.schema,
              revision.schema,
              saved.answers,
              reusableUploads,
            ),
          );
          setUploads(reusableUploads);
          setAttached(reusableUploads);
          setNotice(
            saved.draftKey
              ? "The institution updated this form. Review your retained answers and check whether any documents need to be reattached."
              : "Review the retained draft answers and reattach any documents.",
          );
        }
      })
      .catch((problem) => {
        if (!controller.signal.aborted) {
          setError(formError(problem));
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [applicationId, initialOffering, revision, retry]);
  useEffect(() => {
    if (error) status.current?.focus();
  }, [error]);
  const programmes = current.schema.programmes;
  const chosen = programmes.find((item) => item.offeringId === offering);
  const blocked =
    pending ||
    uploading ||
    loading ||
    loadFailed ||
    Boolean(uncertainSubmission);
  const submitSaved = async (id: number) => {
    try {
      await agentApi.submitApplication(id);
      navigate(`/applications/${id}`);
    } catch (problem) {
      const status = (problem as { response?: { status?: number } } | null)
        ?.response?.status;
      if (!status || status >= 500) {
        setUncertainSubmission(id);
        throw new Error(
          "The submission response was unavailable. Retry to check or finish the submission.",
        );
      }
      setUncertainSubmission(undefined);
      throw problem;
    }
  };
  const retrySubmission = async () => {
    if (!uncertainSubmission) return;
    setPending(true);
    setError("");
    try {
      await submitSaved(uncertainSubmission);
    } catch (problem) {
      setError(formError(problem));
    } finally {
      setPending(false);
    }
  };
  const save = async (submit: boolean) => {
    if (!chosen) {
      setError("Choose a programme before saving");
      return;
    }
    if (submit && !intake) {
      setError("Choose an intake before submitting");
      return;
    }
    setError("");
    setNotice("");
    setPending(true);
    try {
      const saved = await agentApi.saveConfiguredApplication({
        universityId,
        offeringId: offering,
        applicationId: draftId,
        response: {
          revisionId: current.id,
          draftKey,
          intake,
          answers: visibleAnswers(current.schema, answers),
        },
      });
      setDraftId(saved.id);
      setAttached(uploads);
      if (submit) await submitSaved(saved.id);
      else if (!applicationId)
        navigate(`/applications/${saved.id}/edit`, {
          replace: true,
          state: { configuredStep: step },
        });
      else setNotice("Draft saved.");
    } catch (problem) {
      setError(formError(problem));
    } finally {
      setPending(false);
    }
  };
  const refresh = async () => {
    setPending(true);
    setError("");
    try {
      const latest = await agentApi.getApplicationForm(universityId);
      if (!latest.revision) {
        setError(
          "This institution’s form is currently unavailable. Your draft is still saved.",
        );
        return;
      }
      const selected = latest.revision.schema.programmes.find(
        (item) => item.offeringId === offering,
      );
      if (!selected) setOffering(0);
      if (!selected?.intakes.includes(intake)) {
        setIntake("");
        goToStep(1);
      }
      if (latest.revision.id !== current.id) {
        goToStep(1);
        setAnswers(
          keepAnswers(
            current.schema,
            latest.revision.schema,
            answers,
            attached,
          ),
        );
        setUploads(attached);
        setNotice(
          "Form refreshed. Review your retained answers and check whether any documents need to be reattached.",
        );
      } else setNotice("Form is up to date.");
      setCurrent(latest.revision);
    } catch (problem) {
      setError(formError(problem));
    } finally {
      setPending(false);
    }
  };
  const upload = async (fieldId: string, file: File, requestId: string) => {
    if (!chosen) throw new Error("Choose a programme before uploading");
    const data = new FormData();
    Object.entries({
      fieldId,
      universityId,
      offeringId: offering,
      revisionId: current.id,
      draftKey,
      requestId,
    }).forEach(([key, value]) => data.append(key, String(value)));
    data.append("file", file);
    setUploading(true);
    try {
      const result = await agentApi.uploadApplicationFormFile(data);
      stagedFiles.current.set(result.id, file);
      setUploads((previous) => [...previous, result]);
      return result;
    } finally {
      setUploading(false);
    }
  };
  const loadFile = async (file: Upload) => {
    const staged = stagedFiles.current.get(file.id);
    if (staged) return staged;
    if (draftId && attached.some((item) => item.id === file.id))
      return agentApi.downloadApplicationFormFile(draftId, file.id);
    throw new Error("Save the draft before opening this attachment");
  };
  return (
    <section className="container pt-3" style={{ paddingBottom: "5rem" }}>
      <button
        type="button"
        className="btn btn-link ps-0 mb-2"
        disabled={pending || uploading}
        onClick={onBack}
      >
        Back to institution selection
      </button>
      <h1 className="h3">{current.schema.title}</h1>
      {loading && <p role="status">Loading your draft…</p>}
      {error && (
        <div
          ref={status}
          tabIndex={-1}
          role="alert"
          className="alert alert-danger"
        >
          {error}{" "}
          {uncertainSubmission ? (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => void retrySubmission()}
              disabled={pending}
            >
              Retry submission
            </button>
          ) : loadFailed ? (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => {
                setError("");
                setRetry((value) => value + 1);
              }}
            >
              Retry
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => void refresh()}
              disabled={pending}
            >
              Refresh form
            </button>
          )}
        </div>
      )}
      {notice && (
        <p role="status" className="alert alert-info">
          {notice}
        </p>
      )}
      {!loading && !loadFailed && (
        <form
          onSubmit={(event) => event.preventDefault()}
          noValidate
        >
          <div
            ref={stepStart}
            tabIndex={-1}
            aria-label="Application steps"
            className="px-3 pt-3"
          >
            <StepIndicator
              currentStep={step}
              totalSteps={reviewStep}
              stepLabels={stepLabels}
              onStepClick={
                blocked
                  ? undefined
                  : (next) => {
                      if (next < step) goToStep(next);
                    }
              }
            />
            <p className="small text-muted" aria-live="polite">
              Step {step} of {reviewStep} · {stepLabels[step - 1]}
            </p>
          </div>
          {step === 1 && (
            <section className="card mb-3">
              <div className="card-body">
                <h2 className="h5">Programme and intake</h2>
                {!programmes.length && (
                  <p className="text-muted">
                    This form currently has no available programmes and intakes.
                  </p>
                )}
                <div className="row g-3">
                  <div className="col-12 col-md-7">
                    <label
                      htmlFor="configured-programme"
                      className="form-label"
                    >
                      Programme *
                    </label>
                    <Select
                      inputId="configured-programme"
                      instanceId="configured-programme"
                      isDisabled={blocked}
                      isSearchable
                      aria-required
                      options={programmes}
                      getOptionValue={(option) => String(option.offeringId)}
                      value={chosen ?? null}
                      placeholder="Select a programme"
                      onChange={(option) => {
                        setOffering(option?.offeringId ?? 0);
                        setIntake("");
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-5">
                    <label htmlFor="configured-intake" className="form-label">
                      Intake *
                    </label>
                    <Select
                      inputId="configured-intake"
                      instanceId="configured-intake"
                      isDisabled={blocked || !chosen}
                      isSearchable
                      aria-required
                      options={chosen?.intakes.map((value) => ({
                        value,
                        label: value,
                      }))}
                      value={intake ? { value: intake, label: intake } : null}
                      placeholder="Select an intake"
                      onChange={(option) => setIntake(option?.value ?? "")}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
          {step === 2 && (
            <section className="card mb-3">
              <div className="card-body">
                <SavedProfilePicker
                  disabled={blocked}
                  onSelect={(profile) => {
                    const last = profileFill.current;
                    const base = last?.result === answers ? last.base : answers;
                    const filled = fillFromSavedProfile(current.schema, base, profile);
                    if (!filled) {
                      setNotice("This application already has answers. Choose the same student or start a new application to use a different profile.");
                      return false;
                    }
                    profileFill.current = { base, result: filled };
                    setAnswers(filled);
                    setNotice("Profile loaded. Review the prefilled answers and attach any required documents.");
                    return true;
                  }}
                />
              </div>
            </section>
          )}
          {activeSection && (
            <ApplicationFormFields
              key={current.id}
              schema={current.schema}
              sectionId={activeSection.id}
              values={answers}
              onChange={setAnswers}
              files={uploads}
              upload={upload}
              download={async (file) =>
                saveAttachment(await loadFile(file), file.filename)
              }
              disabled={blocked}
            />
          )}
          {step === reviewStep && (
            <section className="card mb-3">
              <div className="card-body">
                <h2 className="h5">Preview application</h2>
                <FormSnapshotSummary
                  loadFile={loadFile}
                  value={{
                    revisionId: current.id,
                    offeringId: offering,
                    schema: current.schema,
                    intake,
                    answers: visibleAnswers(current.schema, answers),
                    uploads,
                  }}
                />
              </div>
            </section>
          )}
          <div className="d-flex flex-wrap gap-2">
            {step > 1 && (
              <button
                type="button"
                className="btn btn-outline-secondary me-auto"
                disabled={blocked}
                onClick={() => goToStep(step - 1)}
              >
                Previous
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={blocked || !chosen}
              onClick={() => void save(false)}
            >
              Save draft
            </button>
            {step === reviewStep ? (
              <button
                key="submit-application"
                type="button"
                className="btn btn-primary"
                disabled={blocked || !chosen}
                onClick={() => void save(true)}
              >
                {pending ? "Working…" : "Submit application"}
              </button>
            ) : (
              <button
                key="advance-step"
                type="button"
                className="btn btn-primary"
                disabled={blocked || (step === 1 && (!chosen || !intake))}
                onClick={() => goToStep(step + 1)}
              >
                {step === reviewStep - 1 ? "Preview application" : "Next"}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
